import React, { useEffect, useMemo, useState } from "react";
import {
  AgGrid,
  Button,
  FileIcon,
  Grid,
  LabeledData,
  Select,
  Tag,
  TextInput,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { ACTION_META, COND_ATTRS, EMAIL_RE } from "./constants";
import * as styles from "./styles_vanilla.css";
import type { ShareEvent, ShareLogEntry, Space, VaultDocument } from "#common/types/vault";
import type { DocumentSharingProps, SharingView } from "./types";

export type { DocumentSharingProps };

const GRID_DEFAULT_COL: AgGrid.ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
  flex: 1,
  minWidth: 120,
};

const SIDE_BAR: AgGrid.SideBarDef = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
    },
    {
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
    },
  ],
};

function condValue(d: VaultDocument, key: string): string | null {
  if (key === "status") return d.status;
  return d.attrs[key] ?? null;
}
function workspaceRecipients(clientDocs: VaultDocument[]): string[] {
  return Array.from(new Set(clientDocs.map((d) => d.attrs.investor).filter(Boolean)))
    .sort()
    .map((i) => `${i} — investor team`);
}

/* ---------------- Recipient picker ---------------- */
type Recip = { mode: "list" | "email"; list: string; email: string };
function recipText(r: Recip) {
  return r.mode === "email" ? r.email.trim() : r.list;
}
function recipValid(r: Recip) {
  return r.mode === "email" ? EMAIL_RE.test(r.email.trim()) : !!r.list;
}
function RecipientPicker({
  recipients,
  value,
  onChange,
}: {
  recipients: string[];
  value: Recip;
  onChange: (r: Recip) => void;
}) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>Recipient</span>
      <div className={styles.recipRow}>
        <div className={styles.recipModes} role="radiogroup" aria-label="Recipient type">
          <button
            type="button"
            role="radio"
            aria-checked={value.mode === "list"}
            className={value.mode === "list" ? styles.recipMode.on : styles.recipMode.off}
            onClick={() => onChange({ ...value, mode: "list" })}
          >
            Select from Investor Team
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={value.mode === "email"}
            className={value.mode === "email" ? styles.recipMode.on : styles.recipMode.off}
            onClick={() => onChange({ ...value, mode: "email" })}
          >
            Enter Email
          </button>
        </div>
        <div className={styles.recipInput}>
          {value.mode === "list" ? (
            <Select
              value={value.list}
              full
              placeholder="Select an investor team"
              onChange={(v) => onChange({ ...value, list: String(v) })}
            >
              <Select.Section>
                {recipients.length === 0 ? (
                  <Select.Option value="">No investor teams in this workspace</Select.Option>
                ) : (
                  recipients.map((r) => (
                    <Select.Option key={r} value={r}>
                      {r}
                    </Select.Option>
                  ))
                )}
              </Select.Section>
            </Select>
          ) : (
            <TextInput
              value={value.email}
              placeholder="name@firm.com"
              onChange={(e) => onChange({ ...value, email: e.target.value })}
            />
          )}
        </div>
      </div>
      {value.mode === "email" && value.email.trim() && !EMAIL_RE.test(value.email.trim()) && (
        <div className={styles.recipErr}>Enter a valid email address.</div>
      )}
    </div>
  );
}

/* ---------------- Matching-documents grid (builder) ---------------- */
type MatchRow = { name: string; file: string; period: string };

/** Builder match-grid name cell: file glyph + name.ext (design appends the extension here). */
function MatchNameCell({ data }: { data?: MatchRow }) {
  if (!data) return null;
  const ext = (data.file || "doc").toLowerCase();
  return (
    <span className={styles.docNameCell}>
      <FileIcon extension={ext} size="medium" />
      <span className={styles.docNameLabel}>
        {data.name}.{ext}
      </span>
    </span>
  );
}
const MATCH_COLS: AgGrid.ColDef<MatchRow>[] = [
  {
    headerName: "Document name",
    field: "name",
    flex: 2,
    minWidth: 200,
    cellRenderer: MatchNameCell,
  },
  { headerName: "Reporting period", field: "period", flex: 1, minWidth: 130 },
];
const MATCH_DEFAULT_COL: AgGrid.ColDef = { sortable: true, resizable: true, filter: true };

/* ---------------- Predicate (live rule) builder ---------------- */
function PredicateForm({
  client,
  clientDocs,
  clients,
  recipients,
  onClientChange,
  onReady,
}: {
  client: string;
  clientDocs: VaultDocument[];
  clients: string[];
  recipients: string[];
  onClientChange: (c: string) => void;
  onReady: (valid: boolean, build: () => ShareEvent) => void;
}) {
  const [conds, setConds] = useState<{ key: string; val: string }[]>([
    { key: "status", val: "External" },
  ]);
  const [recip, setRecip] = useState<Recip>({
    mode: "list",
    list: recipients[0] ?? "",
    email: "",
  });

  const valOpts = (key: string) =>
    Array.from(
      new Set(clientDocs.map((d) => condValue(d, key)).filter((v): v is string => !!v)),
    ).sort();
  const matching = useMemo(
    () => clientDocs.filter((d) => conds.every((c) => !c.val || condValue(d, c.key) === c.val)),
    [clientDocs, conds],
  );
  const ok = matching.length > 0 && recipValid(recip);

  const labelFor = (key: string) => COND_ATTRS.find((a) => a.key === key)?.label ?? key;
  const build = (): ShareEvent => ({
    action: "rule_created",
    client,
    target:
      conds
        .filter((c) => c.val)
        .map((c) => `${labelFor(c.key)} = ${c.val}`)
        .join(" · ") || "All documents",
    recipient: recipText(recip),
    docCount: matching.length,
    conds: conds.filter((c) => c.val).map((c) => ({ a: labelFor(c.key), v: c.val })),
  });

  useEffect(() => {
    onReady(ok, build);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ok, conds, matching.length, recip]);

  const matchRows: MatchRow[] = matching.map((d) => ({
    name: d.name,
    file: d.file,
    period: d.attrs.period ?? "—",
  }));

  return (
    <div className={styles.predCols}>
      <div className={styles.predLeft}>
        <div className={styles.panel}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Live rule details</h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.info}>
              <span className={styles.infoIcon}>
                <Icon type="info-circle" size="small" />
              </span>
              <div>
                Define attribute conditions within {client}. Everyone matching gets access — and
                the set stays current automatically.
              </div>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Client workspace</span>
              <Select value={client} full onChange={(v) => onClientChange(String(v))}>
                <Select.Section>
                  {clients.map((c) => (
                    <Select.Option key={c} value={c}>
                      {c}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
            </div>
            <RecipientPicker recipients={recipients} value={recip} onChange={setRecip} />
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Sharing conditions</h3>
          </div>
          <div className={styles.cardBody}>
            {conds.length > 0 && (
              <div className={styles.condHead} aria-hidden>
                <span className={styles.chCol}>Attribute</span>
                <span className={styles.chOp} />
                <span className={styles.chCol}>Value</span>
                <span className={styles.chDel} />
              </div>
            )}
            {conds.map((c, i) => (
              <div className={styles.condRow} key={i}>
                <div className={styles.condSelectCol}>
                  <Select
                    value={c.key}
                    full
                    onChange={(v) =>
                      setConds((p) =>
                        p.map((x, j) => (j === i ? { key: String(v), val: "" } : x)),
                      )
                    }
                  >
                    <Select.Section>
                      {COND_ATTRS.map((a) => (
                        <Select.Option key={a.key} value={a.key}>
                          {a.label}
                        </Select.Option>
                      ))}
                    </Select.Section>
                  </Select>
                </div>
                <div className={styles.condOpCol}>
                  <Select value="eq" full disabled onChange={() => undefined}>
                    <Select.Section>
                      <Select.Option value="eq">Equals</Select.Option>
                    </Select.Section>
                  </Select>
                </div>
                <div className={styles.condSelectCol}>
                  <Select
                    value={c.val}
                    full
                    placeholder="any"
                    onChange={(v) =>
                      setConds((p) => p.map((x, j) => (j === i ? { ...x, val: String(v) } : x)))
                    }
                  >
                    <Select.Section>
                      {[
                        <Select.Option key="__any" value="">
                          any
                        </Select.Option>,
                        ...valOpts(c.key).map((o) => (
                          <Select.Option key={o} value={o}>
                            {o}
                          </Select.Option>
                        )),
                      ]}
                    </Select.Section>
                  </Select>
                </div>
                <button
                  className={styles.iconBtn}
                  aria-label="Remove condition"
                  onClick={() => setConds((p) => p.filter((_, j) => j !== i))}
                  type="button"
                >
                  <Icon type="xmark" size="small" />
                </button>
              </div>
            ))}
            <button
              className={styles.addCond}
              type="button"
              onClick={() => {
                const used = conds.map((c) => c.key);
                const free = COND_ATTRS.find((a) => !used.includes(a.key)) ?? COND_ATTRS[0];
                setConds((p) => [...p, { key: free.key, val: "" }]);
              }}
            >
              <Icon type="plus" size="small" /> Add condition
            </button>
          </div>
        </div>
      </div>

      <div className={`${styles.panel} ${styles.matchCard}`}>
        <div className={styles.cardHeadRow}>
          <h3 className={styles.cardTitle}>Documents matching sharing conditions</h3>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.info}>
            <span className={styles.infoIcon}>
              <Icon type="info-circle" size="small" />
            </span>
            <div>New documents that match these conditions are shared automatically.</div>
          </div>
          {matching.length === 0 ? (
            <div className={styles.matchEmpty}>No documents match these conditions yet.</div>
          ) : (
            <div className={styles.matchGridBox}>
              <Grid<MatchRow>
                rowData={matchRows}
                columnDefs={MATCH_COLS}
                defaultColDef={MATCH_DEFAULT_COL}
                sideBar={SIDE_BAR}
                domLayout="autoHeight"
                rowHeight={44}
                headerHeight={40}
                suppressCellFocus
              />
              <div className={styles.matchFoot}>Rows: {matching.length}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Collection builder ---------------- */
function CollectionForm({
  client,
  clientDocs,
  clients,
  recipients,
  onClientChange,
  onReady,
}: {
  client: string;
  clientDocs: VaultDocument[];
  clients: string[];
  recipients: string[];
  onClientChange: (c: string) => void;
  onReady: (valid: boolean, build: () => ShareEvent) => void;
}) {
  const externalDocs = useMemo(
    () => clientDocs.filter((d) => d.status === "External"),
    [clientDocs],
  );
  const [name, setName] = useState(`${client} — LP package`);
  const [recip, setRecip] = useState<Recip>({
    mode: "list",
    list: recipients[0] ?? "",
    email: "",
  });
  const [picked, setPicked] = useState<string[]>(() =>
    externalDocs.slice(0, 3).map((d) => d.id),
  );

  const available = externalDocs.filter((d) => !picked.includes(d.id));
  const ok = picked.length > 0 && recipValid(recip);
  const build = (): ShareEvent => ({
    action: "collection_shared",
    client,
    target: name,
    recipient: recipText(recip),
    docCount: picked.length,
    docs: picked
      .map((id) => externalDocs.find((d) => d.id === id)?.name)
      .filter((n): n is string => !!n),
  });

  useEffect(() => {
    onReady(ok, build);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ok, name, picked, recip]);

  return (
    <div className={styles.predCols}>
      <div className={styles.predLeft}>
        <div className={styles.panel}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Collection settings</h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.info}>
              <span className={styles.infoIcon}>
                <Icon type="info-circle" size="small" />
              </span>
              <div>
                Hand-pick specific {client} documents into a named bundle. The set is fixed once
                shared.
              </div>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Client workspace</span>
              <Select value={client} full onChange={(v) => onClientChange(String(v))}>
                <Select.Section>
                  {clients.map((c) => (
                    <Select.Option key={c} value={c}>
                      {c}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
            </div>
            <div className={styles.field}>
              <TextInput
                label="Collection name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <RecipientPicker recipients={recipients} value={recip} onChange={setRecip} />
          </div>
        </div>
      </div>

      <div className={`${styles.panel} ${styles.matchCard}`}>
        <div className={styles.cardHeadRow}>
          <h3 className={styles.cardTitle}>Documents in set ({picked.length})</h3>
        </div>
        <div className={styles.cardBody}>
          {picked.length === 0 ? (
            <div className={styles.matchEmpty}>Empty — add documents below.</div>
          ) : (
            <div className={styles.matchList}>
              {picked.map((id) => {
                const d = externalDocs.find((x) => x.id === id);
                if (!d) return null;
                return (
                  <div className={styles.docRow} key={id}>
                    <Icon type="file-alt" size="small" />
                    <span style={{ flex: 1 }}>{d.name}</span>
                    <button
                      className={styles.iconBtn}
                      aria-label={`Remove ${d.name}`}
                      onClick={() => setPicked((p) => p.filter((x) => x !== id))}
                      type="button"
                    >
                      <Icon type="xmark" size="small" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <Select
            value=""
            full
            placeholder={
              available.length === 0 ? "All External documents added" : "Add a document…"
            }
            onChange={(v) => v && setPicked((p) => [...p, String(v)])}
          >
            <Select.Section>
              {available.map((d) => (
                <Select.Option key={d.id} value={d.id}>
                  {d.name}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Builder takeover ---------------- */
function ShareBuilderTakeover({
  mode,
  documents,
  clients,
  initialClient,
  onShare,
  onClose,
}: {
  mode: "predicate" | "collection";
  documents: VaultDocument[];
  clients: string[];
  initialClient?: string;
  onShare: (ev: ShareEvent) => void;
  onClose: () => void;
}) {
  const [client, setClient] = useState(
    initialClient && clients.includes(initialClient) ? initialClient : (clients[0] ?? ""),
  );
  const [ready, setReady] = useState(false);
  const buildRef = React.useRef<(() => ShareEvent) | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const clientDocs = useMemo(
    () => documents.filter((d) => d.attrs.client === client),
    [documents, client],
  );
  const recipients = useMemo(() => workspaceRecipients(clientDocs), [clientDocs]);
  const onReady = (valid: boolean, build: () => ShareEvent) => {
    buildRef.current = build;
    setReady(valid);
  };
  const save = () => {
    if (ready && buildRef.current) {
      onShare(buildRef.current());
      onClose();
    }
  };

  return (
    <div className={styles.takeover} role="dialog" aria-modal="true" aria-label="Share builder">
      <div className={styles.bar}>
        <div className={styles.barExit}>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Exit" type="button">
            <Icon type="xmark" size="medium" />
          </button>
        </div>
        <h2 className={styles.barTitle}>
          {mode === "predicate" ? "Add live rule" : "Create manual collection"}
        </h2>
      </div>
      <div className={styles.scroll}>
        <div className={styles.content}>
          {mode === "predicate" ? (
            <PredicateForm
              key={client}
              client={client}
              clientDocs={clientDocs}
              clients={clients}
              recipients={recipients}
              onClientChange={setClient}
              onReady={onReady}
            />
          ) : (
            <CollectionForm
              key={client}
              client={client}
              clientDocs={clientDocs}
              clients={clients}
              recipients={recipients}
              onClientChange={setClient}
              onReady={onReady}
            />
          )}
        </div>
      </div>
      <div className={styles.foot}>
        <Button variant="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!ready} onClick={save}>
          {mode === "predicate" ? "Save rule" : "Save and share"}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Share detail drawer ---------------- */
/** Re-evaluate a stored live-rule condition (label + value) against a document,
    mirroring how the builder derives condition values. */
const COND_GET: Record<string, (d: VaultDocument) => string | null> = {
  Entity: (d) => d.attrs.entity ?? null,
  "LP / Investor": (d) => d.attrs.investor ?? null,
  "Reporting Period": (d) => d.attrs.period ?? null,
  "Document Status": (d) => d.status,
};

type DrawerDoc = {
  id: string;
  name: string;
  file: string;
  space: string;
  period: string;
  doc?: VaultDocument;
};

/** Document-name cell: file-type glyph + name, matching the design's grid. */
function DocNameCell({ data }: { data?: DrawerDoc }) {
  if (!data) return null;
  return (
    <span className={styles.docNameCell}>
      <FileIcon extension={(data.file || "doc").toLowerCase()} size="medium" />
      <span className={styles.docNameLabel}>{data.name}</span>
    </span>
  );
}

const DRAWER_DOC_COLS: AgGrid.ColDef<DrawerDoc>[] = [
  {
    headerName: "Document name",
    field: "name",
    flex: 2,
    minWidth: 200,
    cellRenderer: DocNameCell,
  },
  { headerName: "Space", field: "space", flex: 1.1, minWidth: 120 },
  { headerName: "Reporting period", field: "period", flex: 1, minWidth: 120 },
];
const DRAWER_DOC_DEFAULT_COL: AgGrid.ColDef = { sortable: true, resizable: true, filter: true };

/** Deterministic placeholder line widths so the faux preview reads like a page. */
function lineWidths(seed: number, n: number): number[] {
  const out: number[] = [];
  let x = seed;
  for (let i = 0; i < n; i += 1) {
    x = (x * 9301 + 49297) % 233280;
    out.push(62 + (x / 233280) * 36);
  }
  return out;
}

/** Faux-page document preview, opened to the left of the drawer on row click. */
function DocPreviewPane({
  doc,
  spaceName,
  onClose,
}: {
  doc: VaultDocument;
  spaceName: string;
  onClose: () => void;
}) {
  const ext = (doc.file || "doc").toLowerCase();
  const a = doc.attrs;
  const subline = [a.client, a.entity, a.investor].filter(Boolean).join("  ·  ");
  return (
    <aside className={styles.previewPane} role="dialog" aria-label="Document preview">
      <div className={styles.previewHead}>
        <div className={styles.previewLeft}>
          <FileIcon extension={ext} size="medium" />
          <div className={styles.previewName} title={doc.name}>
            {doc.name}.{ext}
          </div>
        </div>
        <div className={styles.previewActs}>
          <button className={styles.closeBtn} aria-label="Download document" type="button">
            <Icon type="download" size="small" />
          </button>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Collapse preview"
            type="button"
          >
            <Icon type="arrow-right-to-line" size="small" />
          </button>
        </div>
      </div>
      <div className={styles.previewBody}>
        <div className={styles.page}>
          <div className={styles.letterhead}>
            <div className={styles.lhMark}>{spaceName !== "—" ? spaceName : "Document"}</div>
            <div className={styles.lhMeta}>{doc.modified}</div>
          </div>
          <h1 className={styles.pageTitle}>{doc.name}</h1>
          {subline && <div className={styles.pvSubline}>{subline}</div>}
          <div className={styles.pvRule} />
          {lineWidths(7, 5).map((w, i) => (
            <div key={`a${i}`} className={styles.pvLine} style={{ width: `${w}%` }} />
          ))}
          <div className={styles.pvGap} />
          {lineWidths(31, 6).map((w, i) => (
            <div key={`b${i}`} className={styles.pvLine} style={{ width: `${w}%` }} />
          ))}
          <div className={styles.pvGap} />
          {lineWidths(53, 4).map((w, i) => (
            <div key={`c${i}`} className={styles.pvLine} style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
    </aside>
  );
}

function ShareDetailDrawer({
  ev,
  documents,
  spaces,
  onViewInDocuments,
  onClose,
}: {
  ev: ShareLogEntry;
  documents: VaultDocument[];
  spaces?: Space[];
  onViewInDocuments?: (client: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"details" | "documents">("details");
  const [preview, setPreview] = useState<VaultDocument | null>(null);
  const gridApiRef = React.useRef<AgGrid.GridApi<DrawerDoc> | null>(null);
  const previewIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (previewIdRef.current) setPreview(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const meta = ACTION_META[ev.action];
  const isLive = meta.tone === "live";
  const conds = ev.conds ?? [];

  const spaceName = (d?: VaultDocument): string => {
    if (!d) return "—";
    return spaces?.find((s) => s.id === d.space)?.name ?? "—";
  };

  const docs = useMemo<DrawerDoc[]>(() => {
    if (isLive) {
      return documents
        .filter(
          (d) =>
            d.attrs.client === ev.client &&
            conds.every((c) => {
              const get = COND_GET[c.a];
              return get ? get(d) === c.v : true;
            }),
        )
        .map((d) => ({
          id: d.id,
          name: d.name,
          file: d.file,
          space: spaceName(d),
          period: d.attrs.period ?? "—",
          doc: d,
        }));
    }
    return (ev.docs ?? []).map((n, i) => {
      const d = documents.find((x) => x.name === n);
      return {
        id: d?.id ?? `n${i}`,
        name: n,
        file: d?.file ?? "doc",
        space: spaceName(d),
        period: d?.attrs.period ?? "—",
        doc: d,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, spaces, ev]);

  // Keep the highlighted row in sync with the open preview (design parity).
  useEffect(() => {
    previewIdRef.current = preview?.id ?? null;
    gridApiRef.current?.redrawRows();
  }, [preview]);

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden />
      {preview && (
        <DocPreviewPane
          doc={preview}
          spaceName={spaceName(preview)}
          onClose={() => setPreview(null)}
        />
      )}
      <aside className={styles.drawer} role="dialog" aria-label="Share detail">
        <div className={styles.drawerHead}>
          <h2 className={styles.drawerTitle} title={ev.recipient}>
            {ev.recipient}
          </h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <Icon type="xmark" size="medium" />
          </button>
        </div>

        <div className={styles.drawerTabs} role="tablist">
          <button
            role="tab"
            type="button"
            aria-selected={tab === "details"}
            className={tab === "details" ? styles.drawerTab.on : styles.drawerTab.off}
            onClick={() => setTab("details")}
          >
            Details
          </button>
          <button
            role="tab"
            type="button"
            aria-selected={tab === "documents"}
            className={tab === "documents" ? styles.drawerTab.on : styles.drawerTab.off}
            onClick={() => setTab("documents")}
          >
            Documents
          </button>
        </div>

        <div className={styles.drawerBody}>
          {tab === "details" ? (
            <>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelHeadTitle}>Share details</h3>
                </div>
                <div className={styles.panelBody}>
                  <div className={styles.kvList}>
                    <LabeledData variant="muted" label="Client workspace" value={ev.client} />
                    <LabeledData variant="muted" label="Recipient" value={ev.recipient} />
                    <LabeledData
                      variant="muted"
                      label="Access type"
                      value={
                        isLive ? "Live rule — set stays current" : "Collection — frozen set"
                      }
                    />
                    <LabeledData
                      variant="muted"
                      label="Shared by"
                      value={ev.role ? `${ev.who} · ${ev.role}` : ev.who}
                    />
                    <LabeledData
                      variant="muted"
                      label="Shared on"
                      value={ev.time ? `${ev.date} · ${ev.time}` : ev.date}
                    />
                  </div>
                </div>
              </div>

              {isLive && (
                <div className={styles.panel}>
                  <div className={styles.panelHead}>
                    <h3 className={styles.panelHeadTitle}>Sharing conditions</h3>
                  </div>
                  <div className={styles.panelBody}>
                    <div className={styles.condInfobox}>
                      <span className={styles.condInfoboxIcon}>
                        <Icon type="circle-dot" size="small" />
                      </span>
                      <span>
                        Live rule — new {ev.client} documents
                        {conds.length > 0 ? " that match" : ""} are shared automatically.
                      </span>
                    </div>
                    {conds.length > 0 ? (
                      <div className={styles.condFlow}>
                        <div className={styles.condFlowLead}>
                          {ev.recipient} sees every <b>{ev.client}</b> document where…
                        </div>
                        <div className={styles.condFlowList}>
                          {conds.map((c, i) => (
                            <div className={styles.condFlowRow} key={i}>
                              <span className={styles.condFlowJoin}>
                                {i === 0 ? "where" : "and"}
                              </span>
                              <span className={styles.condAttr}>{c.a}</span>
                              <span className={styles.condOp}>is</span>
                              <span className={styles.condVal}>
                                <Tag variant="neutral" label={c.v} />
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.condFlow}>
                        <div className={styles.condFlowLead}>
                          {ev.recipient} sees <b>every {ev.client} document</b> — no attribute
                          filters.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <h3 className={styles.panelHeadTitle}>
                  {isLive ? "Matching documents" : "Documents in collection"}
                  <span className={styles.panelHeadSub}> {docs.length}</span>
                </h3>
                {isLive && onViewInDocuments && (
                  <button
                    className={styles.extLink}
                    type="button"
                    onClick={() => onViewInDocuments(ev.client)}
                  >
                    View in Documents
                    <Icon type="external-link" size="small" />
                  </button>
                )}
              </div>
              {docs.length === 0 ? (
                <div className={styles.docEmpty}>No documents currently match.</div>
              ) : (
                <div className={styles.docGridWrap}>
                  <Grid<DrawerDoc>
                    rowData={docs}
                    columnDefs={DRAWER_DOC_COLS}
                    defaultColDef={DRAWER_DOC_DEFAULT_COL}
                    sideBar={SIDE_BAR}
                    domLayout="autoHeight"
                    rowHeight={48}
                    headerHeight={40}
                    suppressCellFocus
                    rowClassRules={{
                      docRowOn: (p) =>
                        !!(
                          p.data &&
                          previewIdRef.current &&
                          p.data.id === previewIdRef.current
                        ),
                    }}
                    onGridReady={(e) => {
                      gridApiRef.current = e.api;
                    }}
                    onRowClicked={(e) => e.data?.doc && setPreview(e.data.doc)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ---------------- Grid row types ---------------- */
type RuleRow = {
  id: string;
  recipient: string;
  conditions: string;
  client: string;
  docCount: number;
  ev: ShareLogEntry;
};
type CollRow = {
  id: string;
  recipient: string;
  collection: string;
  client: string;
  docCount: number;
  ev: ShareLogEntry;
};
type AuditRow = {
  id: string;
  when: string;
  action: string;
  recipient: string;
  client: string;
  docCount: number;
  who: string;
  ts: number;
  ev: ShareLogEntry;
};

/* ---------------- Component ---------------- */
export function DocumentSharing({
  documents,
  log,
  clients,
  spaces,
  view,
  onViewChange,
  onShare,
  onViewInDocuments,
  onExport,
}: DocumentSharingProps) {
  const [innerView, setInnerView] = useState<SharingView>("rules");
  const [builder, setBuilder] = useState<"predicate" | "collection" | null>(null);
  const [detail, setDetail] = useState<ShareLogEntry | null>(null);

  const activeView = view ?? innerView;
  const setView = (v: SharingView) => (onViewChange ? onViewChange(v) : setInnerView(v));
  const clientList = useMemo(
    () =>
      clients ??
      Array.from(new Set(documents.map((d) => d.attrs.client).filter(Boolean))).sort(),
    [clients, documents],
  );

  const rules = useMemo(() => log.filter((e) => e.action === "rule_created"), [log]);
  const collections = useMemo(() => log.filter((e) => e.action === "collection_shared"), [log]);

  const ruleRows: RuleRow[] = rules.map((e) => ({
    id: e.id,
    recipient: e.recipient,
    conditions:
      e.conds && e.conds.length ? e.conds.map((c) => `${c.a} = ${c.v}`).join(" · ") : e.target,
    client: e.client,
    docCount: e.docCount,
    ev: e,
  }));
  const collRows: CollRow[] = collections.map((e) => ({
    id: e.id,
    recipient: e.recipient,
    collection: e.target,
    client: e.client,
    docCount: e.docCount,
    ev: e,
  }));
  const auditRows: AuditRow[] = log.map((e, i) => ({
    id: e.id,
    when: `${e.date} · ${e.time}`,
    action: ACTION_META[e.action].verb,
    recipient: e.recipient,
    client: e.client,
    docCount: e.docCount,
    who: e.who,
    ts: log.length - i,
    ev: e,
  }));

  const ruleCols: AgGrid.ColDef<RuleRow>[] = [
    { headerName: "Recipient", field: "recipient", flex: 1.4, minWidth: 180 },
    { headerName: "Conditions", field: "conditions", flex: 1.8, minWidth: 220 },
    { headerName: "Client", field: "client", flex: 1.2, minWidth: 150 },
    { headerName: "Documents", field: "docCount", width: 130, type: "rightAligned" },
  ];
  const collCols: AgGrid.ColDef<CollRow>[] = [
    { headerName: "Recipient", field: "recipient", flex: 1.4, minWidth: 180 },
    { headerName: "Collection", field: "collection", flex: 1.8, minWidth: 220 },
    { headerName: "Client", field: "client", flex: 1.2, minWidth: 150 },
    { headerName: "Documents", field: "docCount", width: 130, type: "rightAligned" },
  ];
  const auditCols: AgGrid.ColDef<AuditRow>[] = [
    {
      headerName: "Date",
      field: "when",
      flex: 1.1,
      minWidth: 160,
      sort: "desc",
      comparator: (_a, _b, na, nb) => (na.data?.ts ?? 0) - (nb.data?.ts ?? 0),
    },
    { headerName: "Action", field: "action", flex: 1.4, minWidth: 190 },
    { headerName: "Recipient", field: "recipient", flex: 1.4, minWidth: 170 },
    { headerName: "Client", field: "client", flex: 1.2, minWidth: 150 },
    { headerName: "Documents", field: "docCount", width: 130, type: "rightAligned" },
    { headerName: "Shared by", field: "who", flex: 1.1, minWidth: 150 },
  ];

  const tabsList: { key: SharingView; label: string }[] = [
    { key: "rules", label: "Live rules" },
    { key: "collections", label: "Manual collections" },
    { key: "log", label: "Audit log" },
  ];

  const headerAction =
    activeView === "log" ? (
      <Button
        variant="neutral"
        icon="download"
        iconPosition="left"
        onClick={() => onExport?.()}
      >
        Export
      </Button>
    ) : activeView === "collections" ? (
      <Button
        variant="primary"
        icon="plus"
        iconPosition="left"
        onClick={() => setBuilder("collection")}
      >
        Create manual collection
      </Button>
    ) : (
      <Button
        variant="primary"
        icon="plus"
        iconPosition="left"
        onClick={() => setBuilder("predicate")}
      >
        Add live rule
      </Button>
    );

  const emptyState = (title: string, text: string) => (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <Icon type="inbox" size="xx-large" />
      </div>
      <div className={styles.emptyTitle}>{title}</div>
      <div className={styles.emptyText}>{text}</div>
    </div>
  );

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.tabs} role="tablist">
          {tabsList.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={activeView === t.key}
              className={activeView === t.key ? styles.tab.on : styles.tab.off}
              onClick={() => setView(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className={styles.headerAction}>{headerAction}</div>
      </div>

      <div className={styles.results}>
        {activeView === "rules" &&
          (ruleRows.length === 0 ? (
            emptyState(
              "No live rules yet",
              "Add a live rule to share matching documents automatically.",
            )
          ) : (
            <div className={styles.gridWrap}>
              <Grid<RuleRow>
                rowData={ruleRows}
                columnDefs={ruleCols}
                defaultColDef={GRID_DEFAULT_COL}
                sideBar={SIDE_BAR}
                onRowClicked={(e) => e.data && setDetail(e.data.ev)}
              />
            </div>
          ))}
        {activeView === "collections" &&
          (collRows.length === 0 ? (
            emptyState(
              "No collections yet",
              "Create a manual collection to share a fixed set of documents.",
            )
          ) : (
            <div className={styles.gridWrap}>
              <Grid<CollRow>
                rowData={collRows}
                columnDefs={collCols}
                defaultColDef={GRID_DEFAULT_COL}
                sideBar={SIDE_BAR}
                onRowClicked={(e) => e.data && setDetail(e.data.ev)}
              />
            </div>
          ))}
        {activeView === "log" &&
          (auditRows.length === 0 ? (
            emptyState("No activity yet", "Shares you create will appear here.")
          ) : (
            <div className={styles.gridWrap}>
              <Grid<AuditRow>
                rowData={auditRows}
                columnDefs={auditCols}
                defaultColDef={GRID_DEFAULT_COL}
                sideBar={SIDE_BAR}
                onRowClicked={(e) => e.data && setDetail(e.data.ev)}
              />
            </div>
          ))}
      </div>

      {builder && (
        <ShareBuilderTakeover
          mode={builder}
          documents={documents}
          clients={clientList}
          onShare={(ev) => onShare?.(ev)}
          onClose={() => setBuilder(null)}
        />
      )}

      {detail && (
        <ShareDetailDrawer
          ev={detail}
          documents={documents}
          spaces={spaces}
          onViewInDocuments={onViewInDocuments}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
